import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

import React, { useContext, useCallback } from 'react';
import { noop } from "../../utils/noop";
import { useIsomorphicLayoutEffect } from "../useIsomorphicLayoutEffect";
/**
 * Context used to track descendants (children)
 * of something like a MenuList.
 */

var defaultContext = {
  descendants: [],
  registerDescendant: noop,
  unregisterDescendant: noop
};
var DescendantsContext = React.createContext(defaultContext);
/**
 * Child hook for registering itself.
 * @param {String} id
 * @param {Element} element
 * @param {Object} options
 */

export var useDescendant = function useDescendant(id, element) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  var _useContext = useContext(DescendantsContext),
      registerDescendant = _useContext.registerDescendant,
      unregisterDescendant = _useContext.unregisterDescendant,
      descendants = _useContext.descendants;

  useIsomorphicLayoutEffect(function () {
    // Optionally allow the developer to use framer-motion's isPresent
    // context value to remove the descendant before its actually
    // unmounted from the dom.
    if (typeof options.isPresent !== 'undefined' && !options.isPresent) {
      unregisterDescendant(element);
      return noop;
    }

    registerDescendant(id, element, options);
    return function () {
      return unregisterDescendant(element);
    }; // These functions won't change between mount, so we aren't
    // required to include them in our hook dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // TODO: Improve this pattern to intelligently update when
    // options changes. Maybe just JSON.stringify()?
  }, [element, options.isPresent, options.ariaExpanded]);
  var currentIndex = descendants.findIndex(function (item) {
    return item.element === element;
  });
  return {
    registerDescendant: registerDescendant,
    index: currentIndex
  };
};
/**
 * A wrapper for properly registering child descendants
 * in the DescendantsContext.
 */

export var DescendantsProvider = function DescendantsProvider(_ref) {
  var children = _ref.children,
      items = _ref.descendants,
      setItems = _ref.setDescendants;
  var registerDescendant = useCallback(function (id, el, options) {
    setItems(function (existingItems) {
      var registered = existingItems.some(function (_ref2) {
        var element = _ref2.element;
        return element === el;
      });

      if (!registered) {
        // We need to ensure that our descendant is placed at the correct
        // index in instances where delayed mounts / unmounts occur.
        var index = existingItems.findIndex(function (_ref3) {
          var existingElement = _ref3.element; // compareDocumentPosition requires bitwise & operator because
          //  it returns a bitsmask.
          // See: https://developer.mozilla.org/en-US/docs/Web/API/Node/compareDocumentPosition

          return Boolean( // eslint-disable-next-line no-bitwise
          existingElement.current.compareDocumentPosition(el.current) & Node.DOCUMENT_POSITION_PRECEDING);
        });

        var newItem = _objectSpread({
          id: id,
          element: el
        }, options);

        if (index === -1) {
          return [].concat(_toConsumableArray(existingItems), [newItem]);
        }

        return [].concat(_toConsumableArray(existingItems.slice(0, index)), [newItem], _toConsumableArray(existingItems.slice(index)));
      }

      return existingItems;
    }); // We know that setItems won't change between renders, so
    // it's unnecessary to include in our hooks dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var unregisterDescendant = useCallback(function (el) {
    if (!el) {
      return;
    }

    setItems(function (existingItems) {
      return existingItems.filter(function (_ref4) {
        var element = _ref4.element;
        return element !== el;
      });
    }); // setItems won't change, so it doesn't need to be in our deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React.createElement(DescendantsContext.Provider, {
    value: {
      descendants: items,
      registerDescendant: registerDescendant,
      unregisterDescendant: unregisterDescendant
    }
  }, children);
};